'use client';

import { type RefObject, useLayoutEffect, useRef } from 'react';

/**
 * Polyfills `field-sizing: content` for browsers that don't support it (Firefox).
 * Attaches a JS resize listener only when the CSS property is unsupported.
 * No-ops in browsers with native `field-sizing` support (Chrome, Safari).
 *
 * Pass `value` so programmatic updates to controlled inputs (voice-to-text, slot
 * selection) trigger a re-measure in addition to the native `input` event.
 */
export function useFieldSizingFallback(
  ref: RefObject<HTMLTextAreaElement | null>,
  { enabled = true, value }: { enabled?: boolean; value?: unknown } = {},
): void {
  // Stable ref so the value-sync effect below can invoke resize without it
  // being a closure dep (the resize logic captures ref/enabled, not value).
  const resizeRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    if (!enabled) return;
    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content')) return;

    const el = ref.current;
    if (!el) return;

    const resize = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight + 1}px`;
    };

    resizeRef.current = resize;
    resize();
    el.addEventListener('input', resize);
    return () => {
      resizeRef.current = null;
      el.removeEventListener('input', resize);
    };
  }, [ref, enabled]);

  // Re-measure when the controlled value changes programmatically (voice-to-text,
  // slot selection, etc.) — native `input` events don't fire for these.
  // Uncontrolled inputs (value === undefined) are handled entirely by the listener above.
  useLayoutEffect(() => {
    if (value === undefined) return;
    resizeRef.current?.();
  }, [value]);
}
